import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../../../Services/_states';
import { useSnackBar } from '../../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { RECORD_COPY } from '../constants/recordCopy';
import { recordService } from '../services/recordService';
import {
  buildAssignPayload,
  buildUnassignPayload,
  getCurrentAssignees,
  getUserId,
  hasAssignmentChanges,
  isAssignmentApiSuccess,
  normalizeAssignableUsers,
  parseAssignmentApiError,
} from '../utils/leadAssignmentUtils';

function getOrganisationId(user) {
  return user?.profile?.organisation?.organisation_id
    || user?.profile?.organisation_id
    || user?.organisation_id
    || '';
}

export default function useLeadAssignment({
  open,
  campaignId,
  leadIds = [],
  selectedRow = null,
  showCurrentAssignees = false,
  onSuccess,
}) {
  const { showSnackBar } = useSnackBar();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const organisationId = getOrganisationId(loggedInUser);

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [unassignQueue, setUnassignQueue] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const resetState = useCallback(() => {
    setSelectedUsers([]);
    setUnassignQueue([]);
    setAssignedUsers(showCurrentAssignees ? getCurrentAssignees(selectedRow) : []);
  }, [selectedRow, showCurrentAssignees]);

  useEffect(() => {
    if (!open) return undefined;

    resetState();

    if (!organisationId || !campaignId) {
      setUsers([]);
      setLoadingUsers(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingUsers(true);

    recordService.fetchAssignedUsers(organisationId, campaignId)
      .then((list) => {
        if (cancelled) return;
        const normalized = normalizeAssignableUsers(list);
        setUsers(normalized);

        if (!showCurrentAssignees) return;

        setAssignedUsers((current) => current.map((assigned) => {
          const match = normalized.find((user) => getUserId(user) === getUserId(assigned));
          if (!match?.profile_name) return assigned;
          return {
            ...assigned,
            profile_id: assigned.profile_id || match.profile_id,
            profile_name: assigned.profile_name || match.profile_name,
            label: match.label || assigned.label,
          };
        }));
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });

    return () => {
      cancelled = true;
    };
  }, [campaignId, open, organisationId, resetState, showCurrentAssignees]);

  const hasChanges = useMemo(
    () => hasAssignmentChanges({ selectedUsers, unassignQueue }),
    [selectedUsers, unassignQueue]
  );

  const queueUnassign = useCallback((user) => {
    const userId = getUserId(user);
    if (!userId) return;

    setAssignedUsers((current) => current.filter((item) => getUserId(item) !== userId));
    setUnassignQueue((current) => (
      current.some((item) => getUserId(item) === userId)
        ? current
        : [...current, user]
    ));
  }, []);

  const submit = useCallback(async () => {
    if (!leadIds.length) {
      showSnackBar(RECORD_COPY.assign.noLeads, 'warning');
      return { ok: false };
    }

    if (!campaignId) {
      showSnackBar(RECORD_COPY.assign.noCampaign, 'warning');
      return { ok: false };
    }

    if (!hasChanges) {
      showSnackBar(RECORD_COPY.assign.noChanges, 'info');
      return { ok: false };
    }

    setIsSaving(true);

    const operations = [];
    const operationNames = [];

    if (selectedUsers.length > 0) {
      operations.push(
        recordService.assignLeads(buildAssignPayload({
          leadIds,
          campaignId,
          userIds: selectedUsers.map((user) => getUserId(user)).filter(Boolean),
        }))
      );
      operationNames.push('assign');
    }

    if (unassignQueue.length > 0) {
      operations.push(
        recordService.unassignLeads(buildUnassignPayload({
          leadIds,
          campaignId,
          userIds: unassignQueue.map((user) => getUserId(user)).filter(Boolean),
        }))
      );
      operationNames.push('unassign');
    }

    try {
      const results = await Promise.allSettled(operations);
      let successCount = 0;
      const errors = [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && isAssignmentApiSuccess(result.value)) {
          successCount += 1;
          return;
        }

        const failedRes = result.status === 'fulfilled' ? result.value : result.reason;
        errors.push(
          parseAssignmentApiError(
            failedRes,
            operationNames[index] === 'assign'
              ? RECORD_COPY.assign.assignFailed
              : RECORD_COPY.assign.unassignFailed
          )
        );
      });

      if (successCount === operations.length) {
        showSnackBar(RECORD_COPY.assign.saved, 'success');
        onSuccess?.();
        return { ok: true };
      }

      if (successCount > 0) {
        showSnackBar(RECORD_COPY.assign.partialSaved, 'warning');
        onSuccess?.();
        return { ok: true };
      }

      showSnackBar(errors[0] || RECORD_COPY.assign.saveFailed, 'error');
      return { ok: false };
    } catch {
      showSnackBar(RECORD_COPY.assign.saveFailed, 'error');
      return { ok: false };
    } finally {
      setIsSaving(false);
    }
  }, [
    campaignId,
    hasChanges,
    leadIds,
    onSuccess,
    selectedUsers,
    showSnackBar,
    unassignQueue,
  ]);

  return {
    users,
    loadingUsers,
    assignedUsers,
    selectedUsers,
    setSelectedUsers,
    queueUnassign,
    hasChanges,
    isSaving,
    submit,
  };
}
